/**
 * UX-01 freshness indicator — 3-tier visual driven by the API's `cached`
 * variant from `@saledash/shared` (added in Plan 03).
 *
 * Carried forward verbatim from packages/spa/src/components/FreshnessPill.tsx.
 */
import { Tooltip } from './ui/Tooltip.js';

import type { FreshnessPill as FreshnessPillData } from '@saledash/shared';

interface FreshnessPillProps {
  freshness: FreshnessPillData;
}

const STALE_TOOLTIP_COPY =
  "Data may be out of date — the cache hasn't refreshed in over 15 minutes.";

/**
 * Render an `ageSeconds` value as the locked "Updated ..." copy.
 * Exported so the unit tests can lock the boundary cases without rendering
 * the whole pill.
 */
export function formatAge(ageSeconds: number): string {
  const safe = Math.max(0, Math.floor(ageSeconds));
  if (safe < 60) return 'just now';
  const minutes = Math.floor(safe / 60);
  if (minutes >= 60) return 'Updated 60+ min ago';
  return `Updated ${String(minutes)} min ago`;
}

function ariaLabelFor(ageText: string): string {
  return ageText === 'just now' ? 'Updated just now' : ageText;
}

function visualTextFor(ageText: string): string {
  return ageText === 'just now' ? 'Updated just now' : ageText;
}

export function FreshnessPill({ freshness }: FreshnessPillProps): React.JSX.Element {
  if (freshness.status === 'live') {
    return (
      <span
        className="inline-flex min-h-[20px] items-center gap-1.5 rounded-full bg-good-bg px-2.5 py-0.5 text-xs font-semibold text-good-text"
        aria-label="Data is live"
        data-status="live"
      >
        <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-good" />
        Live
      </span>
    );
  }

  if (freshness.status === 'cached') {
    const ageText = formatAge(freshness.ageSeconds);

    if (freshness.tier === 'fresh') {
      return (
        <span
          className="inline-flex min-h-[20px] items-center gap-1.5 rounded-full bg-good-bg px-2.5 py-0.5 text-xs font-semibold text-good-text"
          aria-label={ariaLabelFor(ageText)}
          data-status="cached"
          data-tier="fresh"
          title={freshness.lastRefreshedAt}
        >
          <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-good" />
          {visualTextFor(ageText)}
        </span>
      );
    }

    if (freshness.tier === 'aging') {
      return (
        <span
          className="inline-flex min-h-[20px] items-center gap-1.5 rounded-full bg-warn-bg px-2.5 py-0.5 text-xs font-semibold text-warn-text"
          aria-label={ariaLabelFor(ageText)}
          data-status="cached"
          data-tier="aging"
          title={freshness.lastRefreshedAt}
        >
          <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-warn" />
          {visualTextFor(ageText)}
        </span>
      );
    }

    // tier === 'stale' — red pill + tooltip with locked stale copy.
    return (
      <span
        className="inline-flex items-center gap-1.5"
        data-status="cached"
        data-tier="stale"
      >
        <span
          className="inline-flex min-h-[20px] items-center gap-1.5 rounded-full bg-bad-bg px-2.5 py-0.5 text-xs font-semibold text-bad-text"
          aria-label={ariaLabelFor(ageText)}
          title={freshness.lastRefreshedAt}
        >
          <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-bad" />
          {visualTextFor(ageText)}
        </span>
        <Tooltip trigger="!" label="Stale data warning">
          <p className="font-semibold text-bad-text">Stale data</p>
          <p className="mt-1">{STALE_TOOLTIP_COPY}</p>
        </Tooltip>
      </span>
    );
  }

  if (freshness.status === 'stale') {
    // Legacy Phase-1 variant — retained until the API stops emitting it.
    return (
      <span
        className="inline-flex min-h-[20px] items-center gap-1.5 rounded-full bg-warn-bg px-2.5 py-0.5 text-xs font-semibold text-warn-text"
        aria-label={`Updated ${String(freshness.ageMinutes)} minutes ago`}
        data-status="stale"
      >
        <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-warn" />
        Updated {freshness.ageMinutes} min ago
      </span>
    );
  }

  return (
    <span
      className="inline-flex min-h-[20px] items-center gap-1.5 rounded-full bg-ink-100 px-2.5 py-0.5 text-xs font-semibold text-ink-700"
      aria-label={`Data unavailable: ${freshness.reason}`}
      data-status="unavailable"
    >
      <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-ink-500" />
      Unavailable
    </span>
  );
}
